/**
 * Review Due — the reconsideration watcher.
 *
 * Surfaces the review clocks the record already carries and nothing was
 * actuating: decisions past their review_by_date (migration 464), commitment
 * terms tripped or armed past review_by (migration 680), and confirmed
 * signals past review_by with no response (the signal_events_unanswered view,
 * migration 673). Each becomes a candidate finding for human review —
 * detection proposes; authority disposes (the conditionWatchAnalyzer rule).
 * The job never trips, resolves, reopens, or answers anything.
 *
 * Like Beacon, the batch runner asks the ambient edge function to run the
 * agent now (mode run_agent): the template runtime runs the same scan
 * (supabase/functions/ambient/lib/reviewDue.ts) it runs on the agent's
 * schedule, so findings, dedup bookkeeping and delivery are identical whether
 * scheduled or "Run now". Detection maths is the
 * shared pure core (supabase/functions/_shared/reviewDueCore.ts), which the
 * unit tests exercise directly.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';

export const REVIEW_DUE_AGENT_TYPE = 'review_due';

interface AmbientRunResponse {
  status?: string;
  stats?: Partial<BatchStats>;
  error?: string;
}

/**
 * Run the review-due scan once, server-side. scope/config are accepted for
 * signature parity with the other agents' runners (IntelligenceBatchRunner)
 * but the server run is always workspace-wide and uses deterministic copy —
 * no model is involved.
 */
export async function runReviewDueBatch(
  workspaceId: string,
  _scope: RunScope,
  _config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  if (signal.aborted) return stats;

  onProgress(0, 1);
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.functions.invoke('ambient', {
      // The template runtime runs the agent now, so its delivery settings decide who is told (migration 944).
      body: { mode: 'run_agent', workspace_id: workspaceId, agent_type: 'review_due' },
    });
    if (error) {
      stats.errors++;
      return stats;
    }
    const payload = (data ?? {}) as AmbientRunResponse;
    if (payload.error) {
      stats.errors++;
      return stats;
    }
    stats.processed = Number(payload.stats?.processed) || 0;
    stats.proposed = Number(payload.stats?.proposed) || 0;
    stats.skipped = Number(payload.stats?.skipped) || 0;
    stats.errors = Number(payload.stats?.errors) || 0;
    return stats;
  } catch {
    stats.errors++;
    return stats;
  } finally {
    onProgress(1, 1);
  }
}
